import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from './context/UserContext';
import { ProjectDashboard } from './components/ProjectDashboard';
import { ProjectSetup } from './components/wizard/ProjectSetup';
import { ModelSelection } from './components/wizard/ModelSelection';
import { ScriptInput } from './components/wizard/ScriptInput';
import { CharacterSelection } from './components/wizard/CharacterSelection';
import { Storyboard } from './components/Storyboard';
import { Studio } from './components/Studio';
import { AutoGenerateVideoModal } from './components/AutoGenerateVideoModal';
import { GeneratedImagesPage } from './components/GeneratedImagesPage';
import { SessionNotFound } from './components/SessionNotFound';
import { 
    generateScriptFromAudio, 
    generateCharacters, 
    generateStoryboard, 
    mapEntitiesToShot
} from './services/geminiService';
import { getCredits } from './services/aiService';
import { CREDIT_COSTS } from './types';
import { 
    deductCredits, 
    saveImagesToBackend, 
    saveProjectToBackend, 
    getProjectsFromBackend, 
    loadProjectFromBackend 
} from './services/backendService';
import { 
    saveProjectToIndexedDB, 
    loadProjectFromIndexedDB, 
    deleteProjectFromIndexedDB 
} from './services/dbService';
import { MagicWandIcon } from './components/icons/MagicWandIcon';
import { LoadingSpinner } from './components/icons/LoadingSpinner';
import { SaveIcon } from './components/icons/SaveIcon';
import { DownloadIcon } from './components/icons/DownloadIcon';
import './App.css';

const DEFAULT_CREDITS = 10000;

const getVideoDuration = (src) => {
    return new Promise((resolve) => {
        // If source is missing or invalid, return a reasonable default
        if (!src || typeof src !== 'string') {
            console.warn('⚠️ getVideoDuration: Invalid or missing video source');
            resolve(5); // Default 5 seconds for missing source
            return;
        }

        const video = document.createElement('video');
        video.preload = 'metadata';
        video.crossOrigin = 'anonymous';
        
        let resolved = false;
        
        const cleanup = () => {
            video.onloadedmetadata = null;
            video.ondurationchange = null;
            video.onerror = null;
            video.oncanplay = null;
            video.src = '';
            video.load();
        };
        
        const resolveDuration = (duration, source) => {
            if (resolved) return;
            resolved = true;
            console.log(`✅ Video duration from ${source}:`, duration, 'seconds for', src.substring(0, 60) + '...');
            cleanup();
            resolve(duration);
        };

        const onLoaded = () => {
            if (video.duration && !isNaN(video.duration) && video.duration !== Infinity && video.duration > 0) {
                resolveDuration(video.duration, 'loadedmetadata');
            }
        };
        
        const onDurationChange = () => {
            if (video.duration && !isNaN(video.duration) && video.duration !== Infinity && video.duration > 0) {
                resolveDuration(video.duration, 'durationchange');
            }
        };
        
        const onCanPlay = () => {
            if (video.duration && !isNaN(video.duration) && video.duration !== Infinity && video.duration > 0) {
                resolveDuration(video.duration, 'canplay');
            }
        };

        const onError = (e) => {
            console.warn('⚠️ Video metadata load error:', e?.message || 'unknown error', 'for', src.substring(0, 60));
            if (!resolved) {
                resolved = true;
                cleanup();
                resolve(5); // Default 5 seconds on error
            }
        };

        video.onloadedmetadata = onLoaded;
        video.ondurationchange = onDurationChange;
        video.oncanplay = onCanPlay;
        video.onerror = onError;
        
        // Longer timeout for remote videos (8 seconds)
        setTimeout(() => {
            if (!resolved) {
                console.warn('⚠️ Video duration timeout for:', src.substring(0, 60));
                resolved = true;
                cleanup();
                resolve(5); // Default 5 seconds on timeout
            }
        }, 8000);
        
        video.src = src;
        video.load(); // Explicitly trigger loading
    });
};

const createTimelineFromScenes = async (scenes, existingTimeline) => {
    const tracks = existingTimeline?.tracks || [
        { id: 'track-video', type: 'video', name: 'Video' },
        { id: 'track-audio-music', type: 'audio', name: 'Music' },
        { id: 'track-audio-sfx', type: 'audio', name: 'SFX' },
        { id: 'track-text', type: 'text', name: 'Text' }
    ];
    
    const nonVideoClips = existingTimeline?.clips.filter(c => c.type !== 'video') || [];
    const newVideoClips = [];
    let currentTime = 0;
    let clipIndex = 0;
    const videoTrackId = tracks.find(t => t.type === 'video')?.id || 'track-video';

    for (let sceneIdx = 0; sceneIdx < scenes.length; sceneIdx++) {
        const scene = scenes[sceneIdx];
        for (let shotIdx = 0; shotIdx < (scene.shots || []).length; shotIdx++) {
            const shot = scene.shots[shotIdx];
            // Generate unique ID if shot.id is undefined
            const uniqueShotId = shot.id || `scene${sceneIdx}-shot${shotIdx}-${Date.now()}-${clipIndex}`;
            
            if (shot.video) {
                // Use stored videoDuration if available, otherwise try to get from video element
                let duration;
                if (shot.videoDuration && shot.videoDuration > 0) {
                    duration = shot.videoDuration;
                    console.log(`📹 Using stored duration for shot ${uniqueShotId}:`, duration, 'seconds');
                } else {
                    duration = await getVideoDuration(shot.video);
                    console.log(`📹 Calculated duration for shot ${uniqueShotId}:`, duration, 'seconds');
                }
                
                const videoClip = {
                    id: `clip-video-${uniqueShotId}`,
                    assetId: 'none', 
                    shotId: uniqueShotId,
                    sceneIndex: sceneIdx,
                    shotIndex: shotIdx,
                    type: 'video',
                    subtype: 'video',
                    trackId: videoTrackId,
                    start: currentTime,
                    duration: duration,
                    sourceStart: 0,
                    sourceDuration: duration,
                    transition: { type: 'none', duration: 0 }
                };
                newVideoClips.push(videoClip);
                currentTime += duration;
                clipIndex++;
            } else {
                if (shot.startFrameImage) {
                    const startClip = {
                        id: `clip-image-start-${uniqueShotId}`,
                        assetId: 'none',
                        shotId: uniqueShotId,
                        sceneIndex: sceneIdx,
                        shotIndex: shotIdx,
                        type: 'video',
                        subtype: 'start',
                        trackId: videoTrackId,
                        start: currentTime,
                        duration: 2,
                        sourceStart: 0,
                        sourceDuration: 2
                    };
                    newVideoClips.push(startClip);
                    currentTime += 2;
                    clipIndex++;
                }
                
                if (shot.endFrameImage) {
                    const endClip = {
                        id: `clip-image-end-${uniqueShotId}`,
                        assetId: 'none',
                        shotId: uniqueShotId,
                        sceneIndex: sceneIdx,
                        shotIndex: shotIdx,
                        type: 'video',
                        subtype: 'end',
                        trackId: videoTrackId,
                        start: currentTime,
                        duration: 2,
                        sourceStart: 0,
                        sourceDuration: 2
                    };
                    newVideoClips.push(endClip);
                    currentTime += 2;
                    clipIndex++;
                }
            }
        }
    }

    const allClips = [...newVideoClips, ...nonVideoClips];
    const maxClipEnd = allClips.reduce((max, clip) => Math.max(max, clip.start + clip.duration), 0);
    const totalDuration = Math.max(10, maxClipEnd + 1);

    return {
        tracks,
        clips: allClips,
        duration: totalDuration
    };
};

function App() {
    // Get user data from context
    const { user, loading: userLoading, updateCredits, refreshCredits } = useUser();
    
    const [projects, setProjects] = useState([]);
    const [activeProject, setActiveProject] = useState(null);
    const [step, setStep] = useState('dashboard');
    const [error, setError] = useState(null);
    const [generationState, setGenerationState] = useState('idle');
    const [generationProgress, setGenerationProgress] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    const [showAutoGenerateModal, setShowAutoGenerateModal] = useState(false);
    
    // Use credits from user context
    const [creditsUsed, setCreditsUsed] = useState(() => {
        const stored = localStorage.getItem('user_credits_used');
        return stored ? parseInt(stored, 10) : 0;
    });

    // Fetch credits from server on mount if user is authenticated
    useEffect(() => {
        let isMounted = true;
        let hasFetched = false;
        
        const fetchCredits = async () => {
            if (!user?.isAuthenticated || hasFetched) return;
            hasFetched = true;
            
            try {
                const serverCredits = await getCredits();
                if (isMounted && serverCredits > 0) {
                    updateCredits(serverCredits);
                }
            } catch (e) {
                console.log('Using local/context credits (server unavailable)');
            }
        };
        
        // Only fetch once when user becomes authenticated
        if (user?.isAuthenticated) {
            fetchCredits();
        }
        
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.isAuthenticated]); // Remove updateCredits from deps to prevent infinite loop

    // Save credits used to localStorage
    useEffect(() => {
        localStorage.setItem('user_credits_used', creditsUsed.toString());
    }, [creditsUsed]);

    // Load projects when user is loaded
    useEffect(() => {
        if (user && !userLoading) {
            loadProjects();
        }
        // eslint-disable-next-line
    }, [user?.email, userLoading]);

    const saveProjectToLocal = async (project) => {
        try {
            await saveProjectToIndexedDB(project);
            const listKey = `projects_list_${user?.email || 'guest'}`;
            const existingListStr = localStorage.getItem(listKey);
            let list = existingListStr ? JSON.parse(existingListStr) : [];
            const index = list.findIndex(p => p.id === project.id);
            const metadata = { 
                id: project.id, 
                name: project.name, 
                lastModified: project.lastModified,
                projectId: project.projectId 
            };
            if (index >= 0) list[index] = metadata;
            else list.unshift(metadata);
            localStorage.setItem(listKey, JSON.stringify(list));
            return true;
        } catch (e) {
            console.error("Local save failed:", e);
            return false;
        }
    };

    const loadProjectFromLocal = async (id) => {
        try {
            return await loadProjectFromIndexedDB(id);
        } catch (e) {
            console.error("Local load failed:", e);
            return null;
        }
    };

    const loadProjects = async () => {
        setIsLoadingProjects(true);
        const localListKey = `projects_list_${user?.email || 'guest'}`;
        const localListStr = localStorage.getItem(localListKey);
        let allProjects = localListStr ? JSON.parse(localListStr) : [];

        const result = await getProjectsFromBackend(user?.email);
        if (result && result.success !== false && Array.isArray(result.projects)) {
            const backendProjects = result.projects;
            const backendMap = new Map(backendProjects.map((p) => [p.id, p]));
            const localMap = new Map(allProjects.map(p => [p.id, p]));
            const mergedMap = new Map([...localMap, ...backendMap]);
            allProjects = Array.from(mergedMap.values()).sort((a, b) => b.lastModified - a.lastModified);
        }
        setProjects(allProjects);
        setIsLoadingProjects(false);
    };

    const handleDeductCredits = (amount) => {
        if (!user?.credits || user.credits < amount) {
            alert("Not enough credits!");
            return false;
        }
        if (user.credits >= amount) {
            const newCredits = user.credits - amount;
            updateCredits(newCredits);
            setCreditsUsed(prev => prev + amount);
            
            // Track credits used for the current project
            if (activeProject) {
                const currentProjectCredits = activeProject.creditsUsed || 0;
                setActiveProject(prev => ({
                    ...prev,
                    creditsUsed: currentProjectCredits + amount
                }));
                deductCredits(user?.email, amount, activeProject.projectId || activeProject.id, activeProject.name);
            }
            return true;
        }
        alert("Not enough credits!");
        return false;
    };

    // Track credits used after a successful API call (uses actual credits from backend response)
    const handleTrackCreditsUsed = async (creditsUsed, creditsRemaining, jobId) => {
        if (!creditsUsed || creditsUsed <= 0) return;
        
        console.log(`💳 Credits used: ${creditsUsed}, Remaining: ${creditsRemaining || 'unknown'}, Job ID: ${jobId || 'none'}`);
        
        // Update total credits used
        setCreditsUsed(prev => prev + creditsUsed);
        
        // Track credits for the current project
        if (activeProject) {
            const currentProjectCredits = activeProject.creditsUsed || 0;
            const currentJobIds = activeProject.jobIds || [];
            
            setActiveProject(prev => ({
                ...prev,
                creditsUsed: currentProjectCredits + creditsUsed,
                jobIds: jobId ? [...currentJobIds, jobId] : currentJobIds
            }));
        }
        
        // Update credits from creditsRemaining if available, otherwise refresh from backend
        if (creditsRemaining !== undefined && creditsRemaining !== null) {
            updateCredits(creditsRemaining);
        } else if (refreshCredits) {
            await refreshCredits();
        }
    };

    // Fetch project credits total from backend
    const fetchProjectCredits = async () => {
        if (!activeProject) return;
        
        try {
            const { getProjectCredits } = await import('./services/aiService');
            const projectId = activeProject.projectId || activeProject.id;
            const jobIds = activeProject.jobIds || [];
            
            const result = await getProjectCredits(projectId, jobIds);
            
            if (result.success) {
                console.log(`📊 Project credits: ${result.totalCreditsUsed} used, ${result.availableCredits} available`);
                
                // Update project credits
                setActiveProject(prev => ({
                    ...prev,
                    creditsUsed: result.totalCreditsUsed
                }));
                
                // Update available credits
                updateCredits(result.availableCredits);
            }
        } catch (error) {
            console.error('Failed to fetch project credits:', error);
        }
    };

    const handleSaveMedia = async (mediaData) => {
        if (!activeProject) return;
        saveImagesToBackend(mediaData, user?.email, activeProject.name);
    };

    const handleSaveProject = async () => {
        if (!activeProject) return;
        setIsSaving(true);
        const updatedProject = { ...activeProject, lastModified: Date.now(), currentStep: step };
        setActiveProject(updatedProject);
        await saveProjectToLocal(updatedProject);
        const result = await saveProjectToBackend(updatedProject, user?.email);
        if (result && result.success !== false && result.project_id && result.project_id !== updatedProject.projectId) {
            const projectWithId = { ...updatedProject, projectId: result.project_id };
            setActiveProject(projectWithId);
            await saveProjectToLocal(projectWithId);
            setProjects(prev => prev.map(p => p.id === projectWithId.id ? { ...p, projectId: result.project_id } : p));
        }
        setIsSaving(false);
    };

    const handleCreateProject = () => {
        setActiveProject({
            id: uuidv4(),
            name: '',
            duration: '60 seconds',
            aspectRatio: '16:9',
            script: '',
            characters: [],
            scenes: [],
            assets: [],
            lastModified: Date.now(),
            modelPreferences: {},  // Will be set during model selection step
            currentStep: 'project'
        });
        setStep('project');
    };

    const handleLoadProject = async (id) => {
        setGenerationProgress('Loading project...');
        setGenerationState('transcribing');
        const meta = projects.find(p => p.id === id);
        const backendIdToUse = meta?.projectId || id;
        const backendResult = await loadProjectFromBackend(backendIdToUse, user?.email);
        let projectData = null;
        if (backendResult && backendResult.success && backendResult.project) {
            projectData = backendResult.project;
            if (projectData && !projectData.projectId && meta?.projectId) projectData.projectId = meta.projectId;
        } else {
            projectData = await loadProjectFromLocal(id);
        }
        if (projectData) {
            setActiveProject(projectData);
            if (projectData.currentStep) setStep(projectData.currentStep);
            else if (projectData.scenes?.length > 0) setStep(projectData.timeline?.clips.length > 0 ? 'studio' : 'storyboard');
            else if (projectData.characters?.length > 0) setStep('characters');
            else if (projectData.script) setStep('script');
            else setStep('project');
        }
        setGenerationState('idle');
    };

    const handleDeleteProject = async (id) => {
        setProjects(prev => prev.filter(p => p.id !== id));
        const listKey = `projects_list_${user?.email || 'guest'}`;
        const list = JSON.parse(localStorage.getItem(listKey) || '[]');
        localStorage.setItem(listKey, JSON.stringify(list.filter((p) => p.id !== id)));
        await deleteProjectFromIndexedDB(id);
    };

    const handleProjectSetupSubmit = async (data) => {
        if (!activeProject) return;
        
        // Auto-select default AI models in the background
        try {
            const { fetchAvailableModels } = await import('./services/api');
            const caps = await fetchAvailableModels();
            
            // Get existing selections from localStorage or start fresh
            let selectedModels = {};
            try {
                const stored = localStorage.getItem('selectedAIModels');
                if (stored) selectedModels = JSON.parse(stored);
            } catch (e) {
                // Ignore parse errors
            }
            
            // Auto-select first (default/highest quality) model for each capability
            for (const cap of caps) {
                if (!selectedModels[cap.key] && cap.models && cap.models.length > 0) {
                    // First model in the list is typically the best quality/default
                    const modelKey = cap.models[0].key || cap.models[0].id;
                    selectedModels[cap.key] = modelKey;
                    console.log(`🔧 Auto-selected default model for ${cap.key}:`, cap.models[0].name);
                }
            }
            
            // Save to localStorage
            localStorage.setItem('selectedAIModels', JSON.stringify(selectedModels));
            console.log('💾 Auto-saved model selections:', selectedModels);
            
            // Update project with model preferences and skip to script step
            setActiveProject({ 
                ...activeProject, 
                ...data, 
                modelPreferences: selectedModels,
                lastModified: Date.now(), 
                currentStep: 'script' 
            });
            setStep('script');
        } catch (err) {
            console.warn('Could not auto-select models, continuing anyway:', err.message);
            // Even if model fetching fails, continue to script step
            setActiveProject({ 
                ...activeProject, 
                ...data, 
                lastModified: Date.now(), 
                currentStep: 'script' 
            });
            setStep('script');
        }
    };

    const handleModelSelectionSubmit = (data) => {
        if (!activeProject) return;
        setActiveProject({ ...activeProject, modelPreferences: data, lastModified: Date.now(), currentStep: 'script' });
        setStep('script');
    };

    const handleScriptSubmit = async (data) => {
        if (!activeProject) return;
        setError(null);
        let scriptText = '';
        const conceptOptions = data.conceptOptions || null;
        try {
            if (data.script !== undefined) {
                scriptText = data.script;
            }
            else if (data.audio) {
                setGenerationState('transcribing');
                // Credits are deducted by backend - removed upfront deduction
                scriptText = await generateScriptFromAudio(data.audio.data);
            }
            const updatedProject = { ...activeProject, script: scriptText, conceptOptions, scenes: [], timeline: undefined, lastModified: Date.now(), currentStep: 'characters' };
            setActiveProject(updatedProject);
            if (scriptText.trim() === '') {
                setStep('characters');
                return;
            }
            setGenerationState('character-generating');
            
            // Credits are deducted by backend - removed upfront deduction
            
            console.log('🎭 Generating characters from script...', { scriptLength: scriptText.length, conceptOptions });
            const storyElementsData = await generateCharacters(scriptText);
            console.log('🎭 Raw characters response:', JSON.stringify(storyElementsData, null, 2));
            
            // Handle the new response format: { characters, otherElements, narrators, allItems }
            let charactersArray = [];
            let otherElementsArray = [];
            let narratorsArray = [];
            
            if (storyElementsData && typeof storyElementsData === 'object') {
                if (Array.isArray(storyElementsData.characters)) {
                    charactersArray = storyElementsData.characters;
                }
                if (Array.isArray(storyElementsData.otherElements)) {
                    otherElementsArray = storyElementsData.otherElements;
                }
                if (Array.isArray(storyElementsData.narrators)) {
                    narratorsArray = storyElementsData.narrators;
                }
                if (charactersArray.length === 0 && Array.isArray(storyElementsData.allItems)) {
                    charactersArray = storyElementsData.allItems.filter(item => item.category === 'character');
                    otherElementsArray = storyElementsData.allItems.filter(item => item.category === 'element');
                    narratorsArray = storyElementsData.allItems.filter(item => item.category === 'narrator');
                }
            } else if (Array.isArray(storyElementsData)) {
                charactersArray = storyElementsData.filter(item => item.category === 'character');
                otherElementsArray = storyElementsData.filter(item => item.category === 'element');
                narratorsArray = storyElementsData.filter(item => item.category === 'narrator');
            } else if (!storyElementsData) {
                console.warn('⚠️ No character data received - using empty array');
            } else {
                console.error('❌ Invalid characters data received:', storyElementsData);
                throw new Error('Invalid character data received from backend');
            }
            
            // Auto-add elements based on concept options and script content
            if (conceptOptions) {
                const scriptLower = scriptText.toLowerCase();
                const allNames = [...charactersArray, ...otherElementsArray, ...narratorsArray].map(e => e.name.toLowerCase());

                // If addLogo was selected and no logo element was extracted, add one
                if (conceptOptions.addLogo) {
                    const hasLogo = allNames.some(n => n.includes('logo'));
                    if (!hasLogo) {
                        // Try to extract logo details from the script
                        const logoMatch = scriptText.match(/\*?\*?LOGO\s*PLACEMENT\*?\*?[:\s]*([^\n*]+)/i);
                        const logoDesc = logoMatch 
                            ? `Brand logo. Placement: ${logoMatch[1].trim()}` 
                            : 'Brand logo to be placed on the final images as per the concept directions.';
                        console.log('🏷️ Auto-adding Logo element from concept options');
                        otherElementsArray.push({ name: 'Brand Logo', description: logoDesc, category: 'element' });
                    }
                }

                // If needActor was selected and no actor/character was extracted, add a placeholder
                if (conceptOptions.needActor && charactersArray.length === 0) {
                    const actorMatch = scriptText.match(/\*?\*?ACTOR\s*DIRECTION\*?\*?[:\s]*([^\n*]+(?:\n(?!\*\*)[^\n]+)*)/i);
                    const actorDesc = actorMatch 
                        ? actorMatch[1].trim() 
                        : 'Actor as described in the ad concept.';
                    console.log('🎭 Auto-adding Actor character from concept options');
                    charactersArray.push({ name: 'Actor', description: actorDesc, category: 'character' });
                }
            }

            // Also scan the script for LOGO PLACEMENT even without conceptOptions
            if (!conceptOptions?.addLogo) {
                const scriptHasLogo = /LOGO\s*PLACEMENT/i.test(scriptText);
                const hasLogoElement = otherElementsArray.some(e => e.name.toLowerCase().includes('logo'));
                if (scriptHasLogo && !hasLogoElement) {
                    const logoMatch = scriptText.match(/\*?\*?LOGO\s*PLACEMENT\*?\*?[:\s]*([^\n*]+)/i);
                    const logoDesc = logoMatch 
                        ? `Brand logo. Placement: ${logoMatch[1].trim()}` 
                        : 'Brand logo as mentioned in the concept.';
                    console.log('🏷️ Auto-adding Logo element detected from script text');
                    otherElementsArray.push({ name: 'Brand Logo', description: logoDesc, category: 'element' });
                }
            }
            
            console.log('🎭 Processed arrays:', { 
                charactersCount: charactersArray.length, 
                otherElementsCount: otherElementsArray.length,
                narratorsCount: narratorsArray.length
            });
            
            const elementsWithIds = charactersArray.map((el, index) => ({
                id: `el-${Date.now()}-${index}`,
                name: el.name,
                description: el.description,
                category: el.category || 'character',
                makeupLevel: 'natural',
            }));
            
            const otherElementsWithIds = otherElementsArray.map((el, index) => ({
                id: `elem-${Date.now()}-${index}`,
                name: el.name,
                description: el.description,
                category: el.category || 'element',
            }));
            
            const narratorsWithIds = narratorsArray.map((el, index) => ({
                id: `narr-${Date.now()}-${index}`,
                name: el.name,
                description: el.description,
                category: 'narrator',
                makeupLevel: 'natural',
            }));
            
            const allCharactersWithIds = [...elementsWithIds, ...otherElementsWithIds, ...narratorsWithIds];
            
            console.log('🎭 Characters with IDs:', elementsWithIds);
            console.log('🎨 Other Elements with IDs:', otherElementsWithIds);
            console.log('🎙️ Narrators with IDs:', narratorsWithIds);
            
            setActiveProject(prev => (prev ? { 
                ...prev, 
                characters: allCharactersWithIds,
                conceptOptions,
                currentStep: 'characters' 
            } : null));
            setStep('characters');
        } catch (err) {
            console.error('❌ Error in handleScriptSubmit:', err);
            setError(err.message || 'Generation failed.');
            setStep('script');
        } finally {
            setGenerationState('idle');
        }
    };

    const handleCharactersSubmit = async (characters) => {
        if (!activeProject) return;
        if (!activeProject.scenes || activeProject.scenes.length === 0) {
            if (!activeProject.script || activeProject.script.trim() === '') {
                const blankScene = { 
                    id: `scene-${Date.now()}`, 
                    shots: [{ 
                        id: `shot-${Date.now()}`, 
                        description: 'New shot', 
                        startFramePrompt: '', 
                        endFramePrompt: '', 
                        narration: '', 
                        characterIds: [], 
                        elementIds: [] 
                    }] 
                };
                setActiveProject({ ...activeProject, characters, scenes: [blankScene], lastModified: Date.now(), currentStep: 'storyboard' });
                setStep('storyboard');
                return;
            }
            setGenerationState('storyboarding');
            try {
                const storyboardData = await generateStoryboard({ ...activeProject, characters });
                const scenesWithIds = storyboardData.map((sData, sIdx) => ({
                    id: `scene-${sIdx}-${Date.now()}`,
                    shots: sData.shots.map((shData, shIdx) => {
                        const { characterIds, elementIds } = mapEntitiesToShot(shData, characters);
                        return {
                            id: `shot-${sIdx}-${shIdx}-${Date.now()}`,
                            description: shData.description,
                            startFramePrompt: shData.startFramePrompt,
                            endFramePrompt: shData.endFramePrompt,
                            narration: shData.narration,
                            characterIds,
                            elementIds,
                            dialogues: Array.isArray(shData.dialogue) ? shData.dialogue.map((d) => ({ 
                                characterId: characters.find(c => c.name.toLowerCase() === d.characterName?.toLowerCase())?.id, 
                                text: d.text 
                            })).filter((d) => d.characterId) : []
                        };
                    })
                }));
                setActiveProject({ ...activeProject, characters, scenes: scenesWithIds, lastModified: Date.now(), currentStep: 'storyboard' });
                setStep('storyboard');
            } catch (err) {
                setError("Failed to generate storyboard.");
                setStep('storyboard');
            } finally {
                setGenerationState('idle');
            }
        } else {
            setActiveProject({ ...activeProject, characters, lastModified: Date.now(), currentStep: 'storyboard' });
            setStep('storyboard');
        }
    };

    const handleStoryboardSubmit = async (scenes) => {
        if (!activeProject) return;
        setGenerationProgress('Preparing Studio...');
        setGenerationState('transcribing'); 
        try {
            const timeline = await createTimelineFromScenes(scenes, activeProject.timeline);
            setActiveProject({ ...activeProject, scenes, timeline, lastModified: Date.now(), currentStep: 'studio' });
            setStep('studio');
        } catch (e) {
            setStep('studio');
        } finally {
            setGenerationState('idle');
        }
    };

    const handleProjectUpdate = (updatedProject) => {
        setActiveProject(updatedProject);
    };

    // Handler for "Generate Images" button - navigates to generated images page
    const handleGenerateImages = (characters) => {
        if (!activeProject) return;
        setActiveProject(prev => prev ? { ...prev, characters, lastModified: Date.now() } : null);
        setStep('generated-images');
    };

    // Handler to save generated images to project
    const handleSaveGeneratedImages = (data) => {
        if (!activeProject) return;
        
        console.log('🎯 handleSaveGeneratedImages called with:', {
            generatedImages_count: data.generatedImages?.length || 0,
            generatedAdImages_count: data.generatedAdImages?.length || 0,
            data_keys: Object.keys(data)
        });
        
        const updatedProject = { ...activeProject, ...data, lastModified: Date.now() };
        
        console.log('🎯 Updated project has:', {
            generatedImages: updatedProject.generatedImages?.length || 0,
            generatedAdImages: updatedProject.generatedAdImages?.length || 0
        });
        
        setActiveProject(updatedProject);
        saveProjectToIndexedDB(updatedProject);
        saveProjectToBackend(updatedProject);
    };

    // Handler for "Generate Video" button - opens the auto-generate modal
    const handleGenerateFullVideo = async (characters) => {
        if (!activeProject) return;
        
        // First, ensure characters are saved to the project
        const updatedProject = { ...activeProject, characters, lastModified: Date.now() };
        setActiveProject(updatedProject);
        
        // If no storyboard exists, generate it first
        if (!updatedProject.scenes || updatedProject.scenes.length === 0) {
            if (updatedProject.script && updatedProject.script.trim() !== '') {
                setGenerationState('storyboarding');
                setGenerationProgress('Generating storyboard before video creation...');
                try {
                    const storyboardData = await generateStoryboard({ ...updatedProject, characters });
                    const scenesWithIds = storyboardData.map((sData, sIdx) => ({
                        id: `scene-${sIdx}-${Date.now()}`,
                        shots: sData.shots.map((shData, shIdx) => {
                            const { characterIds, elementIds } = mapEntitiesToShot(shData, characters);
                            return {
                                id: `shot-${sIdx}-${shIdx}-${Date.now()}`,
                                description: shData.description,
                                startFramePrompt: shData.startFramePrompt,
                                endFramePrompt: shData.endFramePrompt,
                                narration: shData.narration,
                                characterIds,
                                elementIds,
                                dialogues: Array.isArray(shData.dialogue) ? shData.dialogue.map((d) => ({ 
                                    characterId: characters.find(c => c.name.toLowerCase() === d.characterName?.toLowerCase())?.id, 
                                    text: d.text 
                                })).filter((d) => d.characterId) : []
                            };
                        })
                    }));
                    
                    const projectWithScenes = { ...updatedProject, characters, scenes: scenesWithIds };
                    setActiveProject(projectWithScenes);
                    setGenerationState('idle');
                    
                    // Now open the auto-generate modal
                    setShowAutoGenerateModal(true);
                } catch (err) {
                    setError("Failed to generate storyboard.");
                    setGenerationState('idle');
                    return;
                }
            } else {
                // No script - create a blank scene
                const blankScene = { 
                    id: `scene-${Date.now()}`, 
                    shots: [{ 
                        id: `shot-${Date.now()}`, 
                        description: 'New shot', 
                        startFramePrompt: '', 
                        endFramePrompt: '', 
                        narration: '', 
                        characterIds: [], 
                        elementIds: [] 
                    }] 
                };
                const projectWithScene = { ...updatedProject, characters, scenes: [blankScene] };
                setActiveProject(projectWithScene);
                alert('Please add shot descriptions in the Storyboard first, then try again.');
                setStep('storyboard');
                return;
            }
        } else {
            // Storyboard exists - open the auto-generate modal directly
            setShowAutoGenerateModal(true);
        }
    };

    // Handle navigation to studio after auto-generation
    const handleNavigateToStudio = (project) => {
        setActiveProject(project);
        setStep('studio');
    };

    const handleExportProject = () => {
        if (!activeProject) return;
        const blob = new Blob([JSON.stringify(activeProject, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${activeProject.name || 'project'}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleUserClick = () => {
        const email = prompt("Enter your email:", user?.email);
        if (email && email.trim()) {
            // TODO: Implement setUserEmail if needed
            console.log('User email changed to:', email.trim());
        }
    };

    const handleImportProject = (file) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const content = e.target?.result;
                const project = JSON.parse(content);
                const importedProject = {
                    ...project,
                    id: uuidv4(),
                    projectId: undefined,
                    lastModified: Date.now()
                };
                setActiveProject(importedProject);
                await saveProjectToLocal(importedProject);
                setStep('project');
            } catch (error) {
                console.error("Error importing project:", error);
                alert("Failed to import project.");
            }
        };
        reader.readAsText(file);
    };

    if (generationState !== 'idle' && generationState !== 'error') {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-[#0a0a0f] text-white">
                <LoadingSpinner className="w-16 h-16 mb-6 text-purple-500" />
                <h2 className="text-2xl font-bold mb-2">Processing...</h2>
                <p className="text-gray-400 mb-8">{generationProgress || 'Creating Magic...'}</p>
            </div>
        );
    }

    // Show "Session not found" screen if user is not authenticated
    if (!user?.isAuthenticated) {
        return <SessionNotFound />;
    }

    return (
        <div className="flex flex-col h-screen bg-[#0a0a0f] text-white">
            <header className="h-16 border-b border-gray-800 flex items-center justify-between px-6 shrink-0 bg-[#101018]">
                <div className="flex items-center gap-4">
                    {/* User Dashboard Button */}
                    <a 
                        href="https://app.loopai.in/dashboard" 
                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg transition-colors text-sm font-medium"
                        data-testid="user-dashboard-btn"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        User Dashboard
                    </a>
                    
                    {/* App Logo */}
                    <div className="flex items-center cursor-pointer" onClick={() => setStep('dashboard')}>
                        <MagicWandIcon className="w-6 h-6 text-purple-500 mr-2" />
                        <h1 className="text-xl font-bold tracking-tight">LoopAi-adstudio</h1>
                    </div>
                </div>
                <div className="flex items-center space-x-6">
                    {activeProject && step !== 'dashboard' && (
                        <>
                            <button onClick={handleExportProject} className="text-gray-400 hover:text-white transition-colors" title="Export Project to JSON">
                                <DownloadIcon className="w-5 h-5" />
                            </button>
                            <button onClick={handleSaveProject} disabled={isSaving} className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded-md transition-colors disabled:opacity-50">
                                {isSaving ? <LoadingSpinner className="w-4 h-4" /> : <SaveIcon className="w-4 h-4" />}
                                <span className="text-sm font-medium">{isSaving ? 'Saving...' : 'Save Project'}</span>
                            </button>
                        </>
                    )}
                    <div className="flex items-center space-x-4">
                        {activeProject && step !== 'dashboard' && (
                            <div className="flex items-center space-x-2 bg-purple-900/30 px-3 py-1 rounded-full border border-purple-700/50">
                                <span className="text-xs font-medium text-purple-400">PROJECT</span>
                                <span className="text-sm font-bold text-purple-300">{(activeProject.creditsUsed || 0).toLocaleString()}</span>
                            </div>
                        )}
                        <div className="flex items-center space-x-2 bg-gray-800/50 px-3 py-1 rounded-full border border-gray-700">
                            <span className="text-xs font-medium text-gray-400">CREDITS</span>
                            <span className="text-sm font-bold text-green-400">{user?.credits?.toLocaleString() || 0}</span>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 cursor-pointer hover:bg-gray-800 rounded-full p-1 pr-3 transition-colors" onClick={handleUserClick}>
                        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-xs font-bold">{user?.name?.charAt(0)?.toUpperCase() || "G"}</div>
                        <span className="text-xs text-gray-400 hidden md:inline max-w-[100px] truncate">{user?.email || "guest@loopai.com"}</span>
                    </div>
                </div>
            </header>

            <main className="flex-grow overflow-hidden relative">
                {step === 'dashboard' && (
                    <ProjectDashboard 
                        projects={projects}
                        onCreate={handleCreateProject}
                        onLoad={handleLoadProject}
                        onDelete={handleDeleteProject}
                        onImport={handleImportProject}
                        onRefresh={loadProjects}
                        isLoading={isLoadingProjects}
                    />
                )}
                {step === 'project' && activeProject && <ProjectSetup initialData={activeProject} onSubmit={handleProjectSetupSubmit} />}
                {step === 'models' && activeProject && <ModelSelection initialData={activeProject} onSubmit={handleModelSelectionSubmit} onBack={() => setStep('project')} />}
                {step === 'script' && activeProject && <ScriptInput initialData={activeProject} onSubmit={handleScriptSubmit} onBack={() => setStep('models')} error={error} onDeductCredits={handleDeductCredits} />}
                {step === 'characters' && activeProject && (
                    <CharacterSelection 
                        initialData={activeProject}
                        onSubmit={handleCharactersSubmit}
                        onBack={() => setStep('script')}
                        error={error}
                        onGenerateFullVideo={handleGenerateFullVideo}
                        onGenerateImages={handleGenerateImages}
                        onDeductCredits={handleDeductCredits}
                        onTrackCreditsUsed={handleTrackCreditsUsed}
                        onSaveMedia={handleSaveMedia}
                        onProjectUpdate={handleProjectUpdate}
                    />
                )}
                {step === 'generated-images' && activeProject && (
                    <GeneratedImagesPage
                        initialData={activeProject}
                        characters={activeProject.characters}
                        onBack={() => setStep('characters')}
                        onSaveProject={handleSaveGeneratedImages}
                    />
                )}
                {step === 'storyboard' && activeProject && (
                    <Storyboard
                        projectData={activeProject}
                        onSubmit={handleStoryboardSubmit}
                        onProjectUpdate={handleProjectUpdate}
                        onBack={() => setStep('characters')}
                        onDeductCredits={handleDeductCredits}
                        onTrackCreditsUsed={handleTrackCreditsUsed}
                        onSaveMedia={handleSaveMedia}
                    />
                )}
                {step === 'studio' && activeProject && <Studio projectData={activeProject} onProjectUpdate={handleProjectUpdate} onBack={() => setStep('storyboard')} />}
            </main>

            {/* Auto Generate Video Modal */}
            {showAutoGenerateModal && activeProject && (
                <AutoGenerateVideoModal
                    projectData={activeProject}
                    onProjectUpdate={handleProjectUpdate}
                    onClose={() => setShowAutoGenerateModal(false)}
                    onTrackCreditsUsed={handleTrackCreditsUsed}
                    onSaveProject={handleSaveProject}
                    onNavigateToStudio={handleNavigateToStudio}
                />
            )}
        </div>
    );
}

export default App;
